"use client";

import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
  Legend,
  ErrorMessage,
  Description,
} from "./catalyst/fieldset";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "./catalyst/dialog";
import { Text, TextLink } from "./catalyst/text";
import { reportViolation } from "@/app/actions";
import { Textarea } from "./catalyst/textarea";
import { usePathname } from "next/navigation";
import { Heading } from "./catalyst/heading";
import { ProductType } from "@/lib/entities";
import { useTranslations } from "next-intl";
import { Select } from "./catalyst/select";
import { Button } from "./catalyst/button";
import { Input } from "./catalyst/input";
import { useActionState } from "react";

interface ReportFormProps {
  contentType: "creator" | "user" | ProductType;
  contentId: number;
  contentName: string;
  open: boolean;
  onSuccess: () => void;
}

export function ReportForm({
  contentType,
  contentId,
  contentName,
  open,
  onSuccess,
}: ReportFormProps) {
  const t = useTranslations("ReportForm");
  const pathname = usePathname();

  const [state, formAction, pending] = useActionState(reportViolation, {
    error: null,
    success: false,
  });

  const contentTypeKey =
    contentType === "creator"
      ? "reportCreator"
      : contentType === "event"
        ? "reportEvent"
        : contentType === "service"
          ? "reportService"
          : "reportUser";

  const violationTypes = [
    { value: "abuse", label: t("violationTypes.abuse") },
    { value: "hate", label: t("violationTypes.hate") },
    { value: "violence", label: t("violationTypes.violence") },
    { value: "illegal", label: t("violationTypes.illegal") },
    { value: "sensitive", label: t("violationTypes.sensitive") },
    { value: "spam", label: t("violationTypes.spam") },
    { value: "copyright", label: t("violationTypes.copyright") },
    { value: "impersonation", label: t("violationTypes.impersonation") },
    { value: "other", label: t("violationTypes.other") },
  ];

  return (
    <Dialog open={open} onClose={onSuccess}>
      <form action={formAction}>
        <DialogTitle>{t(contentTypeKey)}</DialogTitle>
        <DialogDescription>{contentName}</DialogDescription>

        <DialogBody>
          {state.success ? (
            <>
              <Heading>{t("reportSubmitted")}</Heading>
              <Text>{t("reportSubmittedMessage")}</Text>
            </>
          ) : (
            <>
              <input type="hidden" name="contentType" value={contentType} />
              <input type="hidden" name="contentId" value={contentId} />
              <input type="hidden" name="contentName" value={contentName} />
              <input type="hidden" name="pagePath" value={pathname} />

              <Fieldset>
                <Legend>{t("details")}</Legend>
                <Text>
                  {t.rich("reviewMessage", {
                    acceptableUse: (chunks) => (
                      <TextLink href="/acceptable-use">{chunks}</TextLink>
                    ),
                    br: () => <br />,
                  })}
                </Text>
                <FieldGroup>
                  <Field>
                    <Label>{t("violationType")}</Label>
                    <Select name="violationType" required>
                      <option value="">{t("selectViolationType")}</option>
                      {violationTypes.map((type) => (
                        <option key={type.value} value={type.label}>
                          {type.label}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <Field>
                    <Label>{t("description")}</Label>
                    <Textarea
                      name="description"
                      placeholder={t("descriptionPlaceholder")}
                      rows={4}
                      required
                    />
                  </Field>

                  <Field>
                    <Label>{t("evidence")}</Label>
                    <Input
                      name="evidence"
                      type="url"
                      placeholder={t("evidencePlaceholder")}
                    />
                    <Description>{t("evidenceDescription")}</Description>
                  </Field>
                </FieldGroup>
              </Fieldset>

              {state.error && <ErrorMessage>{state.error}</ErrorMessage>}
            </>
          )}
        </DialogBody>

        <DialogActions>
          {state.success ? (
            <Button color="purple" onClick={onSuccess} className="mt-4">
              {t("close")}
            </Button>
          ) : (
            <>
              <Button type="button" plain onClick={onSuccess}>
                {t("cancel")}
              </Button>
              <Button type="submit" disabled={pending} color="red">
                {pending ? t("submitting") : t("submitReport")}
              </Button>
            </>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
